"use client";

import { useState, useEffect } from "react";
import { Box, Button, Container, Grid, TextField, Typography } from "@mui/material";
import contactService from "@/app/_services/contactService";
import posthog from "posthog-js";
import BrandStrip from "../contact/brandStrip";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { WorkWithUsLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import CheckIcon from "@mui/icons-material/Check";

const textPrimary = "#EDEFF2";
const textMuted = "#A7ADB7";

const COLLAB_KIEHLS_URL =
  "https://storage.googleapis.com/cuo-website/best+comedy+near+me.jpg";
const COLLAB_ARIZONA_URL =
  "https://storage.googleapis.com/cuo-website/underground+comedy+.jpg";

const partnerBadgeBoxSx = {
  backgroundColor: "rgba(18,20,24,0.94)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "6px",
  px: { xs: 1.125, sm: 1.375 },
  py: { xs: 0.875, sm: 1 },
  boxShadow: "0 4px 14px rgba(0,0,0,0.38)",
};

const partnerBadgeLabelSx = {
  fontSize: { xs: "0.5rem", sm: "0.5625rem" },
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: textMuted,
  lineHeight: 1.35,
  mb: 0.375,
};

const partnerBadgeNameSx = {
  fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.8125rem" },
  fontWeight: 700,
  color: textPrimary,
  lineHeight: 1.2,
};

export default function WorkWithUs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    posthog.capture("form_opened", { form_type: "work_with_us" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = { name, email, phone_number: phone, comment, form_type: "work_with_us" };
    try {
      const response = await contactService.contactUs(formData);
      const data = await response.json();
      setSuccessMessage(data.message);
      if (data) {
        setName("");
        setEmail("");
        setPhone("");
        setComment("");
        posthog.capture("form_submitted", { form_type: "work_with_us" });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
    }
  };

  /* Fields: dark translucent insets (main); 16px on xs avoids iOS zoom-on-focus */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      borderRadius: "8px",
      "& fieldset": { borderColor: "rgba(255,255,255,0.22)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.38)" },
      "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.48)", borderWidth: 1 },
    },
    "& .MuiInputBase-input": {
      color: "#fff",
      padding: { xs: "12px 14px", sm: "12px 14px" },
      fontSize: { xs: "16px", sm: "1rem" },
    },
    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
  };

  return (
    <>
      <WorkWithUsLayoutHelmet />
      <Header />
      <Box
        component="main"
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >

      {/* A) Hero: Let's Create Something Unforgettable (Padel Haus) - CSS background-image only */}
      <Box
        sx={{
          position: "relative",
          backgroundImage:
            "url('https://storage.googleapis.com/cuo-website/Padel+Haus.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: "min-content", md: "66vh" },
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          py: { xs: 4, md: 2 },
          pb: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.82)",
            zIndex: 0,
          }}
        />
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "left",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: { xs: 4, md: 3 },
            }}
          >
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                maxWidth: { md: "min(52%, 680px)" },
                ml: { xs: -2, md: -5 },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  fontWeight: 600,
                  letterSpacing: { xs: "0.1em", sm: "0.15em" },
                  color: textMuted,
                  textTransform: "uppercase",
                  mb: 1.25,
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                Work With Us
              </Typography>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: "2.125rem", sm: "3rem", md: "3.75rem" },
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  color: textPrimary,
                  mb: 2.5,
                  wordBreak: "break-word",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Let&apos;s Create{" "}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Something{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "#fff",
                      fontStyle: "italic",
                    }}
                  >
                    Unforgettable
                  </Box>
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.0625rem", sm: "1.125rem", md: "1.1875rem" },
                  lineHeight: 1.75,
                  color: textMuted,
                  maxWidth: { xs: "100%", sm: 620 },
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                We create cultural moments that draw large audiences and offer opportunities for brand partnerships and activations. We&apos;re always looking to work with new brands, so don&apos;t be shy!
              </Typography>
            </Box>

            {/* Overlapping collab photos + partner badges — top right on md+ */}
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", sm: 480, md: 448 },
                maxWidth: { xs: 512, sm: "none" },
                height: { xs: 448, sm: 480, md: 512 },
                flexShrink: 0,
                alignSelf: { xs: "center", md: "center" },
                mx: { xs: "auto", md: 0 },
                mr: { md: 3 },
                overflow: "visible",
              }}
              aria-hidden
            >
              {/* Kiehl's — back layer; badge hangs off upper-right */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "62%",
                  aspectRatio: "3 / 4",
                  zIndex: 0,
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "22px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                  }}
                >
                  <Box
                    component="img"
                    src={COLLAB_KIEHLS_URL}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    zIndex: 2,
                    transform: "translate(32%, -32%)",
                    transformOrigin: "top right",
                    maxWidth: "min(92%, 200px)",
                    ...partnerBadgeBoxSx,
                  }}
                >
                  <Typography sx={partnerBadgeLabelSx}>Featured Partner</Typography>
                  <Typography sx={partnerBadgeNameSx}>Kiehl&apos;s</Typography>
                </Box>
              </Box>
              {/* AriZona — front layer; badge hangs off bottom-right */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "72%",
                  aspectRatio: "3 / 4",
                  zIndex: 1,
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "22px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    component="img"
                    src={COLLAB_ARIZONA_URL}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    zIndex: 2,
                    transform: "translate(28%, 28%)",
                    transformOrigin: "bottom right",
                    maxWidth: "min(92%, 200px)",
                    ...partnerBadgeBoxSx,
                  }}
                >
                  <Typography sx={partnerBadgeLabelSx}>Featured Partner</Typography>
                  <Typography sx={partnerBadgeNameSx}>AriZona</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* White brands strip: directly under hero */}
      <BrandStrip />

      {/* Start the Conversation (Wilson + form card) - CSS background-image only */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: "url('https://storage.googleapis.com/cuo-website/Comedy+UO+Wilson+Dec+4+2025.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: "min-content", md: "78vh" },
          py: { xs: 5, md: 7 },
          pb: { xs: 8, md: 10 },
          px: { xs: 2, md: 4 },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.82)", zIndex: 0 }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.75rem" },
              color: textPrimary,
              textAlign: "center",
              mb: 0.5,
              px: { xs: 0.5, md: 0 },
              wordBreak: "break-word",
            }}
          >
            Start the Conversation
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.9375rem", sm: "1rem" },
              color: textMuted,
              textAlign: "center",
              mb: 3,
              px: { xs: 0.5, md: 0 },
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            Tell us about your brand and what you&apos;re looking for.
          </Typography>

          {successMessage && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 3,
                color: "#FFFFFF",
                px: { xs: 1, md: 0 },
                textAlign: "center",
              }}
            >
              <CheckIcon sx={{ color: "#4ade80", fontSize: 24, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: "0.9375rem", sm: "1rem" },
                  color: "#FFFFFF",
                  wordBreak: "break-word",
                }}
              >
                {successMessage}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              maxWidth: 1000,
              width: "100%",
              mx: "auto",
              /* Same frosted “dark clear” panel as Private Events (/contact) form */
              backgroundColor: "rgba(28, 28, 32, 0.58)",
              backdropFilter: "blur(20px) saturate(1.12)",
              WebkitBackdropFilter: "blur(20px) saturate(1.12)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: "12px",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.08), 0 12px 40px rgba(0,0,0,0.4)",
              p: { xs: 2.5, sm: 3, md: 4 },
              boxSizing: "border-box",
              overflowX: "hidden",
              isolation: "isolate",
            }}
          >
            <form onSubmit={handleSubmit}>
              <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                <Grid item xs={12} md={6}>
                  <Typography component="label" sx={{ display: "block", fontSize: "0.875rem", color: "#fff", mb: 0.75 }}>Name</Typography>
                  <TextField
                    fullWidth
                    placeholder="Your name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={inputSx}
                    aria-label="Your name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography component="label" sx={{ display: "block", fontSize: "0.875rem", color: "#fff", mb: 0.75 }}>Email *</Typography>
                  <TextField
                    fullWidth
                    placeholder="you@example.com"
                    variant="outlined"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={inputSx}
                    aria-label="Email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography component="label" sx={{ display: "block", fontSize: "0.875rem", color: "#fff", mb: 0.75 }}>Phone number</Typography>
                  <TextField
                    fullWidth
                    placeholder="(555) 555-5555"
                    variant="outlined"
                    value={phone}
                    type="tel"
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    sx={inputSx}
                    aria-label="Phone number"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography component="label" sx={{ display: "block", fontSize: "0.875rem", color: "#fff", mb: 0.75 }}>Tell us what you&apos;re interested in</Typography>
                  <TextField
                    fullWidth
                    placeholder="Sponsor an upcoming show, throw a custom branded activation, partner with us for a taping..."
                    variant="outlined"
                    multiline
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    sx={inputSx}
                    aria-label="Tell us what you're interested in"
                  />
                </Grid>
                <Grid item xs={12} sx={{ pt: 1, width: "100%" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    className="cuo-public-form-submit"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textTransform: "none",
                      color: "#000",
                      backgroundColor: "rgba(255,255,255,0.9)",
                      borderRadius: "8px",
                      px: 3,
                      py: 1.25,
                      width: { xs: "100%", sm: "auto" },
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": { backgroundColor: "#fff" },
                    }}
                  >
                    Submit Request
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Container>
      </Box>

      </Box>
      <Footer />
    </>
  );
}
